import Editor from '@monaco-editor/react';
import { useEffectOnValueChange } from '@timelyapp/react';
import { clamp } from 'lodash-es';
import type * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import React, { useCallback, useState } from 'react';
import { useCurrentThemeIsDark } from 'utils/theme';
import styles from './MarkdownEditor.module.scss';
import Spinner from './Spinner';

let darkThemeDefined = false;
function ensureDarkTheme(monacoInstance: typeof monaco) {
  if (darkThemeDefined) return;
  monacoInstance.editor.defineTheme('timely-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [],
    colors: {
      'editor.background': '#1C1720',
      'editor.foreground': '#E0DDE4',
    },
  });
  darkThemeDefined = true;
}

interface Props {
  markdown: string;
  onChange: (markdown: string) => void;
}

const MIN_LINES = 2;
const MAX_LINES = 8;
const LINE_HEIGHT = 19;
const VERTICAL_PADDING = 8;
const MarkdownEditor = ({ markdown, onChange }: Props) => {
  const isDark = useCurrentThemeIsDark();
  const [maxSeenLines, setMaxSeenLines] = useState(0);
  const [height, setHeight] = useState(LINE_HEIGHT * 1);
  const [editor, setEditor] = useState<monaco.editor.IStandaloneCodeEditor | null>(null);
  const updateHeight = useCallback(() => {
    if (editor) {
      const lineCount = Math.max(MIN_LINES, editor.getModel()?.getLineCount() ?? 0);
      editor.updateOptions({
        scrollbar: {
          verticalScrollbarSize: lineCount > MAX_LINES ? 4 : 0,
        },
      });
      const newMaxSeenLines = Math.min(MAX_LINES, Math.max(maxSeenLines, lineCount));
      if (newMaxSeenLines !== maxSeenLines) {
        setMaxSeenLines(newMaxSeenLines);
        const scrollable = lineCount > newMaxSeenLines;
        setHeight(
          clamp(lineCount, newMaxSeenLines, MAX_LINES) * LINE_HEIGHT + VERTICAL_PADDING * 2
        );
        if (!scrollable) {
          setTimeout(() => {
            editor.setScrollPosition({ scrollTop: 0 });
          }, 1);
        }
      }
    }
  }, [editor, maxSeenLines]);
  useEffectOnValueChange({ editor, markdown }, updateHeight);
  useEffectOnValueChange(editor, () => {
    if (editor) {
      editor.onKeyDown(e => {
        if (e.code === 'Tab') {
          e.stopPropagation();
        }
        if (e.code === 'Enter' && e.ctrlKey) {
          e.stopPropagation();
        }
      });
    }
  });

  const handleBeforeMount = useCallback((monacoInstance: typeof monaco) => {
    ensureDarkTheme(monacoInstance);
  }, []);

  const monacoTheme = isDark ? 'timely-dark' : 'grayscale';

  return (
    <div className={styles.wrapper}>
      <Editor
        className={styles.editor}
        onMount={setEditor}
        beforeMount={handleBeforeMount}
        height={height}
        language="markdown"
        value={markdown}
        onChange={value => onChange(value ?? '')}
        options={options}
        theme={monacoTheme}
        loading={<Spinner top="10px" />}
      />
    </div>
  );
};

const options = {
  lineNumbers: 'off',
  minimap: { enabled: false },
  folding: false,
  wordWrap: 'on',
  scrollbar: {
    horizontalScrollbarSize: 0,
    verticalScrollbarSize: 0,
    useShadows: false,
  },
  suggest: {
    showWords: false,
  },
  acceptSuggestionOnEnter: 'off',
  padding: {
    top: VERTICAL_PADDING,
    bottom: VERTICAL_PADDING,
  },
  scrollBeyondLastLine: false,
  renderLineHighlight: 'none',
  tabCompletion: 'off',
} as const;

export default MarkdownEditor;
